/**
 * Conferma prima di salvare un fatto: "Salvo che...? Si · No · Correggi".
 * Il memory writer non salva nulla di sensibile senza questa risposta (CLAUDE.md, KB §Fact).
 *
 * `presentation="sheet"` (default) appoggia il foglio in basso sopra la schermata;
 * `presentation="inline"` lo mette nel flusso, per la pagina /kit.
 */
import { useEffect, useRef } from 'react'

import { PrimaryButton } from './PrimaryButton'
import { SecondaryButton } from './SecondaryButton'

export type ConfirmFactSheetProps = {
  /** Il fatto in forma di frase, senza "che" iniziale: "si sveglia verso le 7". */
  statement: string
  onYes?: () => void
  onNo?: () => void
  onCorrect?: () => void
  presentation?: 'sheet' | 'inline'
}

export function ConfirmFactSheet({ statement, onYes, onNo, onCorrect, presentation = 'sheet' }: ConfirmFactSheetProps) {
  const headingRef = useRef<HTMLHeadingElement>(null)

  // Il foglio prende il focus: chi usa lo screen reader sente subito la domanda.
  useEffect(() => {
    if (presentation === 'sheet') headingRef.current?.focus()
  }, [presentation])

  const body = (
    <div
      role={presentation === 'sheet' ? 'dialog' : 'group'}
      aria-modal={presentation === 'sheet' ? true : undefined}
      aria-labelledby="conferma-fatto-titolo"
      className={[
        'surface-card px-[22px] pb-[22px] pt-[24px]',
        presentation === 'sheet' ? 'rounded-t-card' : 'rounded-card',
      ].join(' ')}
    >
      <h2
        id="conferma-fatto-titolo"
        ref={headingRef}
        tabIndex={-1}
        className="text-band font-bold text-ink"
      >
        Salvo che {statement}?
      </h2>
      <PrimaryButton onClick={onYes}>Sì</PrimaryButton>
      <div className="mt-3 flex flex-wrap gap-2.5">
        <SecondaryButton grow onClick={onNo}>
          No
        </SecondaryButton>
        <SecondaryButton grow onClick={onCorrect}>
          Correggi
        </SecondaryButton>
      </div>
    </div>
  )

  if (presentation === 'inline') return body

  return (
    <div className="fixed inset-0 z-40 flex items-end bg-[rgba(43,45,49,0.35)]">
      <div className="mx-auto w-full max-w-[430px]">{body}</div>
    </div>
  )
}
